import React from 'react';

import { autoPlacement, offset, Placement } from '@floating-ui/react';
import { showModal } from 'modal.react.js';
import { components, ValueContainerProps } from 'react-select-5';

import Button from 'components/shared/Button';
import area from 'components/shared/GlobalModalArea/area';
import Icon from 'components/shared/Icon';
import ArrowDown from 'components/shared/Icon/icons/ArrowDown';
import Search from 'components/shared/Icon/icons/Search';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { FinalFormSelectProps } from 'components/shared/Select/adapters/FinalForm/types';
import { DeleteAction } from 'components/shared/Select/components/DeleteAction';
import { OptionWithAction } from 'components/shared/Select/options/OptionWithAction';
import { useEmailTemplateMutations } from 'hooks/useEmailTemplateMutations';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { usePopover } from 'hooks/usePopover';

import { FormValues } from '../types';
import { ConfirmationModal } from './ConfirmationModal';
import s from './index.module.css';

function DropdownSelect(props: FinalFormSelectProps<FormValues['template']>) {
  const { isMobile } = useMediaBreakpoints();
  const { deleteEmailTemplateMutation } = useEmailTemplateMutations();
  const allowedPlacements: Placement[] | undefined = isMobile ? undefined : ['bottom-end'];
  const { Popover, toggleTooltip, setReference, closeTooltip } = usePopover({
    options: {
      middleware: [offset(6), autoPlacement({ alignment: 'end', allowedPlacements })],
      strategy: 'fixed',
    },
  });
  const OptionWithDeleteMemoized = React.useCallback((optionProps: any) => {
    const onClickDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      openConfirmationModal({ onConfirm: () => deleteEmailTemplateMutation.mutate(optionProps.data.value) });
    };

    return <OptionWithAction {...optionProps} action={<DeleteAction onClick={onClickDelete} />} />;
  }, []);

  const maxMenuHeight = isMobile ? undefined : 150;
  const template = props.value as FormValues['template'];

  return (
    <div>
      {Popover({
        children: (
          <Select
            maxMenuHeight={maxMenuHeight}
            placeholder="Search..."
            classNames={{
              indicatorSeparator: () => s.indicatorSeparator,
              valueContainer: () => s.valueContainer,
              control: () => s.control,
              container: () => s.container,
              menu: () => s.menu,
              singleValue: () => s.singleValue,
            }}
            menuIsOpen
            autoFocus={!isMobile}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: null,
              IndicatorsContainer: () => null,
              ValueContainer,
              Option: OptionWithDeleteMemoized,
            }}
            onKeyDown={(e) => e.key === 'Escape' && closeTooltip()}
            {...props}
            blurInputOnSelect
            onBlur={closeTooltip}
          />
        ),
      })}

      <div ref={setReference}>
        <Button
          className={s.button}
          onClick={toggleTooltip}
          type="default"
          htmlType="button"
          disabled={props.isDisabled}
        >
          <span className={s.buttonText}>{template?.name ?? 'Select Template'}</span>

          <Icon component={ArrowDown} width="2rem" height="2rem" />
        </Button>
      </div>
    </div>
  );
}

async function openConfirmationModal({ onConfirm }: { onConfirm: () => void }) {
  await showModal(area, (res, rej) => (
    <ConfirmationModal
      onClose={() => rej(null)}
      onConfirm={() => {
        onConfirm();
        res();
      }}
    />
  ));
}

function ValueContainer({ children, ...props }: ValueContainerProps<FormValues['template']>) {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        {!!children && (
          <div className={s.searchIconContainer}>
            <Icon component={Search} width="2rem" height="2rem" />
          </div>
        )}
        {children}
      </components.ValueContainer>
    )
  );
}

export { DropdownSelect };
